import { groups as groupConfigs, pageDefinitions, sectionMeta } from 'virtual:docs-index';

/** Default content group — preserves the original single-group `/docs` API. */
export const DEFAULT_GROUP = 'documentation';

export interface DocGroup {
  key: string;
  /** Site-absolute base route, e.g. `/developers`. */
  basePath: string;
  /** Directory under `src/content` backing the group. */
  dir: string;
}

export interface DocPage {
  slug: string;
  section: string;
  group: string;
  /** Version segment for versioned sections (e.g. 'v1'); undefined otherwise. */
  version?: string;
  /**
   * API versions an unversioned page belongs to (frontmatter `versions:`), e.g.
   * a v1-only page in the developers Overview. Undefined means every version.
   */
  versions?: string[];
  title: string;
  description: string;
  order: number;
  icon: string;
  contentUrl: string;
}

export interface DocSection {
  slug: string;
  group: string;
  /** Version segment for versioned sections (e.g. 'v1'); undefined otherwise. */
  version?: string;
  title: string;
  order: number;
  pages: DocPage[];
}

interface SectionMeta {
  slug: string;
  title: string;
  order: number;
  version?: string;
}

interface Frontmatter {
  title: string;
  description: string;
  order: number;
  icon: string;
}

export interface DocPageWithContent extends DocPage {
  content: string;
}

const groupDirByKey = new Map<string, string>(groupConfigs.map((g) => [g.key, g.dir]));

// Markdown and images live anywhere under src/content (one subdirectory per
// group). Vite emits each file as a hashed asset; we map paths/basenames to the
// built URLs so content can be fetched lazily and image references rewritten.
const markdownUrlModules = import.meta.glob<string>('/src/content/**/*.md', {
  query: '?url',
  eager: true,
  import: 'default',
});

// Doc images live in `<group>/images` and are referenced from markdown as
// `images/<name>.png`. Map basename -> built URL so we can rewrite the relative
// paths to real URLs at render time.
const imageUrlModules = import.meta.glob<string>('/src/content/*/images/*', {
  query: '?url',
  eager: true,
  import: 'default',
});

const imageUrlByName = new Map<string, string>();
for (const [path, url] of Object.entries(imageUrlModules)) {
  const name = path.split('/').pop();
  if (name) imageUrlByName.set(name, url);
}

function resolveImageUrls(markdown: string): string {
  return markdown.replace(
    /!\[([^\]]*)\]\(images\/([^)\s]+)\)/g,
    (match, alt: string, name: string) => {
      const url = imageUrlByName.get(name);
      return url ? `![${alt}](${url})` : match;
    },
  );
}

function parseFrontmatter(raw: string): { frontmatter: Frontmatter; content: string } {
  const match = raw.match(/^---\n([\s\S]*?)\n---\n([\s\S]*)$/);
  if (!match) {
    return {
      frontmatter: { title: 'Untitled', description: '', order: 99, icon: '' },
      content: raw,
    };
  }

  const yamlBlock = match[1];
  const content = match[2].trimStart();
  const frontmatter: Record<string, string | number> = {};

  for (const line of yamlBlock.split('\n')) {
    const colonIdx = line.indexOf(':');
    if (colonIdx === -1) continue;
    const key = line.slice(0, colonIdx).trim();
    let value: string | number = line.slice(colonIdx + 1).trim();
    if (/^\d+$/.test(value)) value = parseInt(value, 10);
    frontmatter[key] = value;
  }

  return {
    frontmatter: {
      title: String(frontmatter.title || 'Untitled'),
      description: String(frontmatter.description || ''),
      order: Number(frontmatter.order ?? 99),
      icon: String(frontmatter.icon || ''),
    },
    content,
  };
}

function getDocPath(
  group: string,
  sectionSlug: string,
  pageSlug: string,
  version?: string,
): string {
  const dir = groupDirByKey.get(group);
  if (!dir) return '';
  const versionSegment = version ? `${version}/` : '';
  return `/src/content/${dir}/${versionSegment}${sectionSlug}/${pageSlug}.md`;
}

function buildSections(group: string, version?: string): DocSection[] {
  const meta = (sectionMeta[group] ?? []).filter((m) => m.version === version);
  const pages = (pageDefinitions[group] ?? []).filter((p) => p.version === version);
  const sectionMap = new Map<string, { meta: SectionMeta; pages: DocPage[] }>();

  for (const sectionMetaValue of meta) {
    sectionMap.set(sectionMetaValue.slug, { meta: sectionMetaValue, pages: [] });
  }

  for (const pageDef of pages) {
    const section = sectionMap.get(pageDef.section);
    if (!section) continue;

    const docPath = getDocPath(group, pageDef.section, pageDef.slug, version);
    const contentUrl = markdownUrlModules[docPath];
    if (!contentUrl) continue;

    section.pages.push({
      ...pageDef,
      group,
      contentUrl,
    });
  }

  const sections: DocSection[] = [];
  for (const [slug, { meta: sectionMetaValue, pages: sectionPages }] of sectionMap) {
    sectionPages.sort((a, b) => a.order - b.order);
    sections.push({
      slug,
      group,
      version,
      title: sectionMetaValue.title,
      order: sectionMetaValue.order,
      pages: sectionPages,
    });
  }

  sections.sort((a, b) => a.order - b.order);
  return sections;
}

const _cachedSections = new Map<string, DocSection[]>();

/** All top-level docs-center groups, in their configured order. */
export function getGroups(): DocGroup[] {
  return groupConfigs.map((g) => ({ key: g.key, basePath: g.basePath, dir: g.dir }));
}

export function getGroup(groupKey: string): DocGroup | undefined {
  return getGroups().find((g) => g.key === groupKey);
}

export function getAllSections(
  group: string = DEFAULT_GROUP,
  version?: string,
): DocSection[] {
  const cacheKey = `${group}::${version ?? ''}`;
  let sections = _cachedSections.get(cacheKey);
  if (!sections) {
    sections = buildSections(group, version);
    _cachedSections.set(cacheKey, sections);
  }
  return sections;
}

// Sections shown on the developers landing and sidebar: the unversioned sections
// (e.g. Overview) followed by one version's API sections (the latest by default),
// flattened and ordered. Each section carries its own `version`, so links resolve
// to the right URL. The version landing (/developers/v1) passes its own version.
// Unversioned sections keep only the pages meant for this version (frontmatter
// `versions:`), so the shared Overview can differ between v1 and v2.
export function getDeveloperSections(version: string): DocSection[] {
  const unversioned = getAllSections('developers')
    .map((section) => ({
      ...section,
      pages: section.pages.filter((page) => isPageForVersion(page, version)),
    }))
    .filter((section) => section.pages.length > 0);

  return [...unversioned, ...getAllSections('developers', version)].sort(
    (a, b) => a.order - b.order,
  );
}

function isPageForVersion(page: DocPage, version: string): boolean {
  return !page.versions || page.versions.includes(version);
}

/**
 * The API version an unversioned developers page is pinned to, when its
 * `versions:` frontmatter names exactly one. The sidebar uses this to stay on
 * that version's navigation, since these pages carry no version in their URL.
 */
export function getDeveloperPageVersion(
  sectionSlug: string | undefined,
  pageSlug: string | undefined,
): string | undefined {
  if (!sectionSlug || !pageSlug) return undefined;
  const versions = getPage(sectionSlug, pageSlug, 'developers')?.versions;
  return versions?.length === 1 ? versions[0] : undefined;
}

export function getSection(
  sectionSlug: string,
  group: string = DEFAULT_GROUP,
  version?: string,
): DocSection | undefined {
  return getAllSections(group, version).find((s) => s.slug === sectionSlug);
}

export function getPage(
  sectionSlug: string,
  pageSlug: string,
  group: string = DEFAULT_GROUP,
  version?: string,
): DocPage | undefined {
  const section = getSection(sectionSlug, group, version);
  return section?.pages.find((p) => p.slug === pageSlug);
}

const pageContentCache = new Map<string, string>();

export async function getPageContent(
  sectionSlug: string,
  pageSlug: string,
  group: string = DEFAULT_GROUP,
  version?: string,
): Promise<string | undefined> {
  const page = getPage(sectionSlug, pageSlug, group, version);
  if (!page) return undefined;

  const cacheKey = `${group}/${version ?? ''}/${sectionSlug}/${pageSlug}`;
  const cachedContent = pageContentCache.get(cacheKey);
  if (cachedContent) return cachedContent;

  const response = await fetch(page.contentUrl);
  if (!response.ok) return undefined;

  const rawMarkdown = await response.text();
  const { content } = parseFrontmatter(rawMarkdown);
  const resolved = resolveImageUrls(content);
  pageContentCache.set(cacheKey, resolved);
  return resolved;
}

export async function getPageWithContent(
  sectionSlug: string,
  pageSlug: string,
  group: string = DEFAULT_GROUP,
  version?: string,
): Promise<DocPageWithContent | undefined> {
  const page = getPage(sectionSlug, pageSlug, group, version);
  if (!page) return undefined;

  const content = await getPageContent(sectionSlug, pageSlug, group, version);
  if (!content) return undefined;

  return {
    ...page,
    content,
  };
}

export interface TocHeading {
  id: string;
  text: string;
  level: number;
}

export function extractHeadings(markdown: string): TocHeading[] {
  const headings: TocHeading[] = [];
  const lines = markdown.split('\n');

  for (const line of lines) {
    const match = line.match(/^(#{2,3})\s+(.+)$/);
    if (match) {
      const level = match[1].length;
      const text = match[2].trim();
      const id = text
        .toLowerCase()
        .replace(/[^\w\s-]/g, '')
        .replace(/\s+/g, '-');
      headings.push({ id, text, level });
    }
  }

  return headings;
}
